import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { applyTooltips, Tooltip, DEFINITIONS } from '../utils/tooltips'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import { CHAPTERS, CHAPTER_CONTENT, CHAPTER_FEEDBACK_TITLES, CHAPTER_BOTTOM_NAV, CHAPTER_KEY_TERMS } from '../data/chapters'
import FeedbackCard, { KeyTermsCard } from './FeedbackCard'

function ChapterViewer({ chapterId, state, onQuizPass, onBack, speech, onNavigate, onPractice, visuals }) {
  const chapter = CHAPTERS.find(c => c.id === chapterId);
  const content = CHAPTER_CONTENT[chapterId];
  const [section, setSection] = useState(() => {
    // Prefer in-memory practiceReturnTab when returning from a practice view
    if (state && state.practiceReturnView === `chapter_${chapterId}` && state.practiceReturnTab != null) {
      return state.practiceReturnTab;
    }
    const lsKey = chapterId === 'ch04' ? 'tlaw_ch04_return_section' : chapterId === 'ch05' ? 'tlaw_ch05_return_section' : chapterId === 'ch06' ? 'tlaw_ch06_return_section' : null;
    if (lsKey) {
      try {
        const stored = localStorage.getItem(lsKey);
        if (stored !== null) {
          localStorage.removeItem(lsKey);
          const v = parseInt(stored, 10);
          return isNaN(v) ? 0 : v;
        }
      } catch {}
    }
    return 0;
  });
  const [quizQ, setQuizQ]         = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizFeed, setQuizFeed]   = useState(false);
  const [score, setScore]         = useState(null);
  const [speakingSection, setSpeakingSection] = useState(null);

  if (!content || !chapter) {
    return (
      <div style={{ padding: '40px 24px', maxWidth: '600px', margin: '0 auto' }}>
        <button style={{ ...btnGhost, marginBottom: '24px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
        <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <p style={{ ...serif, fontSize: '18px', color: V.muted, fontStyle: 'italic' }}>This chapter is loading — please return to the Dashboard and try again.</p>
          <button style={{ ...btnGold, marginTop: '20px' }} onClick={onBack}>← Return to Dashboard</button>
        </div>
      </div>
    );
  }

  const inQuiz = section >= content.sections.length;

  // Reset quiz state and section to first tab whenever the chapter changes.
  // The lazy useState initializer only runs on initial mount of ChapterViewer —
  // when navigating from one chapter to another the component instance is reused
  // and chapterId changes as a prop, so we need this effect to reset state.
  // The localStorage-based return pattern (ch04/ch05) is preserved because it
  // only runs in the lazy initializer on the very first mount for that chapterId.
  useEffect(() => {
    setSection(0);
    setQuizQ(0);
    setQuizAnswers({});
    setQuizFeed(false);
    setScore(null);
    setSpeakingSection(null);
  }, [chapterId]);

  useEffect(() => { if (!speech?.speaking) setSpeakingSection(null); }, [speech?.speaking]);

  useEffect(() => {
    if (!speech?.enabled || !inQuiz || score !== null) return;
    speech.speak(content.quiz[quizQ].q);
  }, [quizQ, inQuiz, speech?.enabled, score]);

  useEffect(() => {
    if (!quizFeed || !speech?.enabled || score !== null) return;
    const q = content.quiz[quizQ];
    const picked = quizAnswers[quizQ];
    if (picked !== undefined && picked !== q.correct) speech.speak(q.explain);
  }, [quizFeed, speech?.enabled]);

  const resetQuiz = () => { setQuizQ(0); setQuizAnswers({}); setQuizFeed(false); setScore(null); };

  const pickAnswer = (oi) => {
    if (quizFeed) return;
    setQuizAnswers(a => ({ ...a, [quizQ]: oi }));
    setQuizFeed(true);
  };

  const advanceQuiz = () => {
    const nxt = quizQ + 1;
    if (nxt < content.quiz.length) {
      setQuizQ(nxt);
      setQuizFeed(false);
    } else {
      const allA = { ...quizAnswers };
      let correct = 0;
      content.quiz.forEach((q, i) => { if (allA[i] === q.correct) correct++; });
      const pct = Math.round((correct / content.quiz.length) * 100);
      setScore(pct);
      if (pct >= 75) setTimeout(() => onQuizPass(chapterId, pct), 1500);
    }
  };

  return (
    <div style={{ padding: '28px 40px', maxWidth: '860px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ ...eyebrow, marginBottom: '8px' }}>Chapter {chapter?.num}</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, margin: '0 0 4px' }}>{chapter?.name}</h2>
      <p style={{ fontSize: '13px', color: V.muted, marginBottom: '24px' }}>{chapter?.sub}</p>

      {CHAPTER_KEY_TERMS[chapterId] && (
        <KeyTermsCard terms={CHAPTER_KEY_TERMS[chapterId]} onNavigate={onNavigate} />
      )}

      {/* Ch02 What You Will Learn card */}
      {chapterId === 'ch02' && (
        <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
          <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.muted, textTransform:'uppercase', letterSpacing:'2px', marginBottom:'8px' }}>CHAPTER 02 — READING PRICE</div>
          <h3 style={{ ...serif, fontSize:'24px', color:V.gold, fontWeight:400, margin:'0 0 16px' }}>What You Will Learn</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>
            {[
              'How to read candlestick charts and what each candle is telling you',
              'How to identify trend direction using price structure alone',
              'How to read volume and what it confirms about price movement',
              'How to recognize chart patterns and what they signal',
            ].map((obj,i) => (
              <div key={i} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                <span style={{ color:V.sage, fontWeight:600, flexShrink:0, marginTop:'1px' }}>✓</span>
                <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.text, lineHeight:1.6 }}>{obj}</span>
              </div>
            ))}
          </div>
          <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'16px 0' }} />
          <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
            Price is the most honest data on any chart. Every indicator in this program is derived from price. Learn to read price first.
          </p>
        </div>
      )}

      {/* Ch04 bridge from Ch03 + What You Will Learn card */}
      {chapterId === 'ch04' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 10px' }}>
              In Chapter 3 you learned to measure momentum — how stretched the rubber band is and in which direction.
            </p>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 10px' }}>
              Now you will learn to read the structure of the market — WHERE the rubber band has anchored before and is most likely to anchor again.
            </p>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:0 }}>
              <span style={{ color:V.goldL, fontStyle:'normal' }}>Structure tells you WHERE.</span> <span style={{ color:V.muted, fontStyle:'italic' }}>Momentum tells you HOW STRONG.</span> <span style={{ color:V.text }}>Together they tell you WHEN.</span>
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
            <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.muted, textTransform:'uppercase', letterSpacing:'2px', marginBottom:'8px' }}>CHAPTER 04 — READING STRUCTURE</div>
            <h3 style={{ ...serif, fontSize:'24px', color:V.gold, fontWeight:400, margin:'0 0 16px' }}>What You Will Learn</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>
              {[
                'How to identify support and demand zones on any chart',
                'How to identify resistance and supply zones on any chart',
                'How to read Open Interest and use it to confirm liquidity',
                'How to combine structure and momentum into one complete read — The Confluence',
                'How to draw your structural levels in ThinkorSwim with confidence',
              ].map((obj,i) => (
                <div key={i} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                  <span style={{ color:V.sage, fontWeight:600, flexShrink:0, marginTop:'1px' }}>✓</span>
                  <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.text, lineHeight:1.6 }}>{obj}</span>
                </div>
              ))}
            </div>
            <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'16px 0' }} />
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
              In Chapter 2 you learned HOW to draw lines in ThinkorSwim. Now you will learn exactly WHERE to draw them and why those levels matter.
            </p>
          </div>
        </>
      )}

      {/* Ch05 bridge from Ch04 + What You Will Learn card */}
      {chapterId === 'ch05' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 10px' }}>
              In Chapter 4 you learned to read the structure of the market — where buyers and sellers have drawn their battle lines.
            </p>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 10px' }}>
              Before you place your first trade you need to understand one more thing: how options are priced and what forces change that price.
            </p>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:0 }}>
              The Greeks are the answer. They are the four forces that determine how much an option is worth — and how that value changes as the market moves.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
            <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.muted, textTransform:'uppercase', letterSpacing:'2px', marginBottom:'8px' }}>CHAPTER 05 — THE GREEKS</div>
            <h3 style={{ ...serif, fontSize:'24px', color:V.gold, fontWeight:400, margin:'0 0 16px' }}>What You Will Learn</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>
              {[
                'What Delta tells you about probability and strike selection',
                'What Theta tells you about time decay and why sellers have an edge',
                'What Vega tells you about volatility and when to sell',
                'What Gamma tells you about risk near expiration',
                'How the Greeks work together to price every option you trade',
              ].map((obj,i) => (
                <div key={i} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                  <span style={{ color:V.sage, fontWeight:600, flexShrink:0, marginTop:'1px' }}>✓</span>
                  <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.text, lineHeight:1.6 }}>{obj}</span>
                </div>
              ))}
            </div>
            <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'16px 0' }} />
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
              The Greeks are not complicated. Each one answers a specific question about your position. Learn what question each one answers and you will understand options pricing better than most retail traders.
            </p>
          </div>
        </>
      )}

      {/* Ch06 bridge from Ch05 + What You Will Learn card */}
      {chapterId === 'ch06' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              You understand Delta and Theta. You know how time decay works, how probability shapes your edge, and what the Greeks are telling you about every position.
            </p>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0' }}>
              Now put them to work. Chapter 6 is your first selling strategy — the Cash Secured Put. This is where knowledge becomes income.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ ...eyebrow, color: V.gold, marginBottom: '12px' }}>WHAT YOU WILL LEARN</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['How Cash Secured Puts generate consistent income','Why selling puts is a high-probability strategy','How to select the right strike using Delta','How to manage and roll a position','The Bull Put Spread as a defined-risk version'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: V.gold, marginTop: '1px', flexShrink: 0 }}>›</span>
                  <span style={{ fontSize: '14px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ch07 bridge from Ch06 + What You Will Learn card */}
      {chapterId === 'ch07' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              You can sell Cash Secured Puts and collect premium. You understand how to use Delta to select strikes, how theta decay works in your favor, and how to manage a position.
            </p>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0' }}>
              Now learn the other side of the Wheel. The Covered Call is how you generate income from stock you already own — or stock you were assigned.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ ...eyebrow, color: V.gold, marginBottom: '12px' }}>WHAT YOU WILL LEARN</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['How Covered Calls generate income from existing stock','How to select the right strike and expiration','How the Bear Call Spread limits risk','How assignment works and when to welcome it','How Covered Calls connect to the Wheel strategy'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: V.gold, marginTop: '1px', flexShrink: 0 }}>›</span>
                  <span style={{ fontSize: '14px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ch08 bridge from Ch07 + What You Will Learn card */}
      {chapterId === 'ch08' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              You can sell puts. You can sell calls. You understand assignment, premium collection, and the role of Delta and Theta in every trade.
            </p>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0' }}>
              Chapter 8 connects these strategies into one continuous system — the Wheel. This is the core of income-based options trading.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ ...eyebrow, color: V.gold, marginBottom: '12px' }}>WHAT YOU WILL LEARN</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['How the Wheel strategy combines CSPs and Covered Calls','How to manage the full cycle of a Wheel trade','How to roll positions when the market moves against you','How to calculate your true cost basis','When to exit the Wheel and take profits'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: V.gold, marginTop: '1px', flexShrink: 0 }}>›</span>
                  <span style={{ fontSize: '14px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ch09 bridge from Ch08 + What You Will Learn card */}
      {chapterId === 'ch09' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              You have a complete trading strategy. You know the Wheel — how to collect premium, how to manage assignment, how to roll positions, and how to profit in multiple market conditions.
            </p>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0' }}>
              Chapter 9 is about protecting what you build. Risk management is the skill that separates profitable traders from those who give it all back.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ ...eyebrow, color: V.gold, marginBottom: '12px' }}>WHAT YOU WILL LEARN</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['How to size positions to protect your account','How to set rules before you trade — not during','How to use stop losses and profit targets consistently','How to recover from a losing trade without revenge trading','How to build a risk framework you can follow forever'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: V.gold, marginTop: '1px', flexShrink: 0 }}>›</span>
                  <span style={{ fontSize: '14px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ch10 bridge from Ch09 + What You Will Learn card */}
      {chapterId === 'ch10' && (
        <>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              You have a complete toolkit. Strategy, structure, risk management — the technical foundation is in place.
            </p>
            <p style={{ ...serif, fontSize: '15px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0' }}>
              Chapter 10 is the hardest chapter. Trading psychology is what separates traders who last from those who don't. The market will test everything you have learned. This chapter teaches you how to pass that test.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
            <p style={{ ...eyebrow, color: V.gold, marginBottom: '12px' }}>WHAT YOU WILL LEARN</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['Why your mindset is your most important trading tool','How to identify and interrupt emotional trading patterns','How to build a pre-trade routine that protects your capital','How to journal effectively to accelerate your growth','How to develop the trader identity that sustains long-term success'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span style={{ color: V.gold, marginTop: '1px', flexShrink: 0 }}>›</span>
                  <span style={{ fontSize: '14px', color: V.muted, lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Ch03 bridge from Ch02 + What You Will Learn card */}
      {chapterId === 'ch03' && (
        <>
          <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '16px 20px', marginBottom: '20px' }}>
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:0 }}>
              In Chapter 2 you learned to read raw price — candlesticks, trend direction, volume, and patterns. Now you will add momentum indicators to your reading. Moving Averages and RSI measure the same rubber band from a different angle.
            </p>
          </div>
          <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '12px', padding: '24px', marginBottom: '28px' }}>
            <div style={{ fontFamily:"'Jost',sans-serif", fontSize:'10px', color:V.muted, textTransform:'uppercase', letterSpacing:'2px', marginBottom:'8px' }}>CHAPTER 03 — READING MOMENTUM</div>
            <h3 style={{ ...serif, fontSize:'24px', color:V.gold, fontWeight:400, margin:'0 0 16px' }}>What You Will Learn</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>
              {[
                'Why momentum indicators exist and what problem they solve',
                'How the 20, 50, and 200-day Moving Averages work and what each one tells you',
                'How to identify the CSP entry zone using the 20-day MA',
                'How RSI measures whether price is stretched up or down',
                'How to combine MA and RSI to find the highest-probability entry',
                'How to recognize choppy markets and when to stand aside',
              ].map((obj,i) => (
                <div key={i} style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                  <span style={{ color:V.sage, fontWeight:600, flexShrink:0, marginTop:'1px' }}>✓</span>
                  <span style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.text, lineHeight:1.6 }}>{obj}</span>
                </div>
              ))}
            </div>
            <div style={{ height:'0.5px', background:'rgba(201,168,76,0.15)', margin:'16px 0' }} />
            <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'12px', color:V.muted, fontStyle:'italic', lineHeight:1.75, margin:0 }}>
              Every indicator is measuring the rubber band. Learning two is enough. More is not better.
            </p>
          </div>
        </>
      )}

      {/* Section nav */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {content.sections.map((s, i) => (
          <button key={i} style={{ padding: '6px 14px', borderRadius: '7px', border: `0.5px solid ${section === i ? V.gold : V.border}`, background: section === i ? 'rgba(201,168,76,0.1)' : 'transparent', color: section === i ? V.goldL : V.muted, fontSize: '12px', cursor: 'pointer' }}
            onClick={() => { setSection(i); resetQuiz(); }}>{s.title}</button>
        ))}
        <button style={{ padding: '6px 14px', borderRadius: '7px', border: `0.5px solid ${inQuiz ? V.gold : V.border}`, background: inQuiz ? 'rgba(201,168,76,0.1)' : 'transparent', color: inQuiz ? V.goldL : V.muted, fontSize: '12px', cursor: 'pointer' }}
          onClick={() => setSection(content.sections.length)}>Test Your Knowledge ✎</button>
      </div>

      {!inQuiz ? (
        <div>
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', gap: '12px' }}>
              <h3 style={{ ...serif, fontSize: '22px', fontWeight: 500 }}>{content.sections[section].title}</h3>
              {speech?.enabled && (
                <button onClick={() => {
                  if (speakingSection === section && speech.speaking) { speech.stop(); return; }
                  setSpeakingSection(section);
                  speech.speak(content.sections[section].title + '. ' + content.sections[section].body);
                }} style={{ flexShrink: 0, background: 'none', border: `0.5px solid ${speakingSection === section && speech.speaking ? 'rgba(201,168,76,0.5)' : V.border}`, borderRadius: '6px', cursor: 'pointer', padding: '4px 9px', fontSize: '14px', color: speakingSection === section && speech.speaking ? V.gold : V.muted, boxShadow: speakingSection === section && speech.speaking ? '0 0 8px rgba(201,168,76,0.25)' : 'none', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span>{speakingSection === section && speech.speaking ? '⏹' : '🔊'}</span>
                  {speakingSection === section && speech.speaking && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: V.gold, display: 'inline-block', animation: 'pulse 1s infinite' }} />}
                </button>
              )}
            </div>
            {content.sections[section].body && <p style={{ fontSize: '15px', lineHeight: 1.85, color: 'rgba(240,237,230,0.85)' }}>{applyTooltips(content.sections[section].body)}</p>}
            {(() => { const Vis = visuals[`${chapterId}:${section}`]; return Vis ? <Vis onNavigate={onNavigate} onPractice={onPractice} chapterId={chapterId} section={section} /> : null; })()}
          </div>

          {chapterId === 'ch03' && section === 2 && (
        <>
          <div style={{ borderLeft: `3px solid ${V.gold}`, background: V.bg2, borderRadius: '0 8px 8px 0', padding: '14px', marginTop: '20px' }}>
            <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
              Moving Averages and RSI measure price momentum. When you reach Chapter 6 you will add one more reading: IV Rank — which measures options premium conditions.
              Together they form your complete pre-trade checklist: trend confirmed (MAs), rubber band stretched (RSI), premium worth selling (IV Rank above 30).
            </p>
          </div>
          <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, fontWeight: 700, marginBottom: '10px', letterSpacing: '0.02em' }}>
              You Are Asking the Right Question
            </div>
            <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              {`If you are wondering: "I understand each piece — but when and where do I actually enter a trade?"\n\nYou are exactly where you should be.\n\nThat question gets answered completely in Chapter 4 (The Confluence) and Chapter 6 (Your First CSP).\n\nEvery indicator you are learning right now is one more piece of the rubber band reading.\n\nWhen they all align — that is your entry.\n\nKeep going. You are closer than you think.`}
            </p>
          </div>
        </>
      )}


      {chapterId === 'ch08' && section === 5 && (
        <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>When You Are Ready to Scale</div>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{`Right now your only job is to master one position.\n\nOne entry. One management decision.\nOne exit. One full cycle.\n\nWhen you have done that —\nand done it again —\nand done it a third time —\n\nPortfolio management awaits you in Tier 2 Advanced Concepts.\n\nThat is where you learn to run multiple positions simultaneously, manage correlation risk, and build a diversified options portfolio.\n\nFor now — one position.\nMaster the wheel completely.\nThe rest follows naturally.`}</p>
        </div>
      )}

      {chapterId === 'ch09' && section === 2 && (
        <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px', marginTop: '20px' }}>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>Your Daily Routine — One Position</div>
          {[
            { time: 'Before market open (2 minutes)', steps: 'Check your open position.\nNote the DTE.\nIs it approaching 21 days?\nCheck for earnings on your stock.' },
            { time: 'Market open (2 minutes)', steps: 'Did it move significantly overnight?\nIs it at 50% profit? Close it.\nIs it at 2x loss? Close it.\nEverything else — hold and monitor.' },
            { time: 'Before market close (2 minutes)', steps: 'Where does it stand?\nAny action needed before close?\nLog your observation.' },
          ].map((row, i) => (
            <div key={i} style={{ marginBottom: i < 2 ? '14px' : 0, paddingBottom: i < 2 ? '14px' : 0, borderBottom: i < 2 ? `0.5px solid rgba(240,237,230,0.06)` : 'none' }}>
              <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.goldL, fontWeight: 600, marginBottom: '5px' }}>{row.time}</div>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{row.steps}</p>
            </div>
          ))}
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, marginTop: '14px' }}>
            Total: 6 minutes per day. Options selling is not day trading. Theta is doing the work. You are just checking in.
          </p>
          <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '8px', padding: '14px', marginTop: '14px' }}>
            <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
              When you are ready to manage multiple positions simultaneously — Portfolio Management is waiting for you in Tier 2.
            </p>
          </div>
        </div>
      )}

      {chapterId === 'ch09' && section === 3 && (
            <div style={{ marginTop: '20px' }}>

              {/* Opening card */}
              <div style={{ background: V.bg2, borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 500, color: V.gold, marginBottom: '14px', lineHeight: 1.35 }}>
                  Is Your Trading Capital Truly Risk Capital?
                </h3>
                {[
                  'Risk capital is money whose loss would not change your life.',
                  'Not your emergency fund.\nNot your rent money.\nNot money you need in six months.\nNot borrowed money you are counting on to grow.',
                  'True risk capital sits in your trading account while your life runs normally on other income and other assets.',
                  'This is not about account size. A $5,000 account funded from genuine surplus is better positioned than a $50,000 account funded from savings you cannot afford to lose.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Five Questions */}
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', fontWeight: 500, color: V.gold, marginBottom: '16px', lineHeight: 1.35 }}>
                Five Questions Before You Fund a Trading Account
              </h3>
              {[
                {
                  num: '01',
                  q: 'Do you have 3–6 months of living expenses in a separate emergency fund?',
                  a: 'Your emergency fund and your trading account should never be the same money. Ever. If they are — fund the emergency account first. Trade later.',
                  border: V.gold,
                },
                {
                  num: '02',
                  q: 'Do you have income — job, business, real estate — that covers your life regardless of how trading goes?',
                  a: 'Trading income is variable income. Variable income cannot be your only income until you have years of consistent results behind you. Build your trading alongside stable income — not instead of it.',
                  border: V.gold,
                },
                {
                  num: '03',
                  q: 'If this entire account went to zero tomorrow, would your life change materially?',
                  a: 'Answer honestly. If the answer is yes — you are not trading risk capital. You are trading survival capital. That changes your psychology in ways no strategy can overcome.',
                  border: V.gold,
                },
                {
                  num: '04',
                  q: 'Are you trading this money because you want to grow it — or because you need it to grow to pay for something?',
                  a: 'Needing a trade to work is the most dangerous state in trading. It overrides every rule you have. Trading from need is not trading. It is gambling with extra steps.',
                  border: V.gold,
                },
                {
                  num: '05',
                  q: 'Have you done enough paper trading to know your own behavioral patterns before risking real capital?',
                  a: 'Paper trading is not just about testing your strategy. It is about learning how YOU behave when trades go against you. Do you follow your rules under pressure? Do you know yet? Find out on paper before you find out with real money.',
                  border: V.sage,
                  aColor: V.sage,
                },
              ].map(card5 => (
                <div key={card5.num} style={{ background: V.bg3, border: `0.5px solid ${card5.border === V.sage ? 'rgba(122,171,138,0.3)' : 'rgba(201,168,76,0.18)'}`, borderRadius: '8px', padding: '16px', marginBottom: '10px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '6px' }}>{card5.num}</div>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.text, lineHeight: 1.6, marginBottom: '8px' }}>{card5.q}</div>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: card5.aColor || V.muted, lineHeight: 1.7, margin: 0 }}>{card5.a}</p>
                </div>
              ))}

              {/* Results framework */}
              <div style={{ background: V.bg2, padding: '20px', borderRadius: '10px', marginTop: '20px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, fontWeight: 600, marginBottom: '14px' }}>What Your Answers Mean</div>
                {[
                  {
                    color: V.sage,
                    text: 'Five yes answers: You are trading from solid ground. Your psychology has the foundation it needs. Focus on execution.',
                  },
                  {
                    color: V.gold,
                    text: 'Three or four yes answers: You are mostly ready. Identify the gaps and address them. Start with smaller position sizes while you build the missing pieces.',
                  },
                  {
                    color: V.rose,
                    text: 'Fewer than three yes answers: Be honest with yourself. The missing pieces will show up in your trading as broken rules, emotional decisions, and inconsistent results. Address the foundation first. The market will still be here.',
                  },
                ].map((row, i) => (
                  <div key={i} style={{ borderLeft: `3px solid ${row.color}`, paddingLeft: '14px', marginBottom: i < 2 ? '12px' : 0 }}>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75, margin: 0 }}>{row.text}</p>
                  </div>
                ))}
              </div>

              {/* Forward bridge */}
              <div style={{ background: V.bg2, borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '16px', marginTop: '20px' }}>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
                  In Chapter 10 you will learn why this financial foundation is not just practical advice — it is nervous system preparation.
                  {'\n\n'}Your biology responds to financial insecurity in ways that override every rule you know.
                  {'\n\n'}Solid ground is the first and most powerful inner work practice available to you.
                </p>
              </div>

            </div>
          )}

          {chapterId === 'ch09' && section === 4 && (
            <div style={{ marginTop: '20px' }}>

              {/* Opening card */}
              <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 500, color: V.gold, marginBottom: '10px', lineHeight: 1.35 }}>
                  Options Trading and Taxes
                </h3>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
                  Not tax advice. Essential awareness. Know this before your first profitable year surprises you.
                </p>
              </div>

              {/* Disclaimer */}
              <div style={{ background: V.bg2, border: `1px solid rgba(196,122,122,0.35)`, borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
                  Nothing in this section is tax advice. Tax laws change and vary by jurisdiction. Consult a qualified tax professional — ideally one who has experience with options traders — before making any tax-related decisions. This section exists so you are not surprised by concepts you have never heard of.
                </p>
              </div>

              {/* Basic Framework */}
              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>HOW OPTIONS PROFITS ARE TAXED</div>
                {[
                  'In the United States options trading profits are generally treated as capital gains.',
                  'SHORT TERM CAPITAL GAINS:\nPositions held less than one year.\nTaxed at your ordinary income rate.\nMost options trades fall here.',
                  'LONG TERM CAPITAL GAINS:\nPositions held more than one year.\nLower tax rate — typically 15–20% for most traders.\nLess common for active options sellers since most trades expire within 30–45 days.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* 60/40 Rule */}
              <div style={{ background: V.bg2, border: `1px solid rgba(122,171,138,0.3)`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.sage, fontWeight: 700, marginBottom: '10px' }}>The 60/40 Rule for Index Options</div>
                {[
                  'This is one of the most favorable tax treatments in trading — and most traders do not know it exists.',
                  'For broad-based index options (SPX, NDX, RUT) Section 1256 of the tax code applies:\n\n60% of profits are treated as long-term capital gains.\n40% are treated as short-term capital gains.',
                  'This applies REGARDLESS of how long you held the position. Even a one-day SPX trade gets the 60/40 treatment.',
                  'For traders in higher tax brackets this can mean significantly lower taxes on index options profits compared to individual stock options.',
                  'This is one reason experienced traders gravitate toward SPX as their primary vehicle.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '10px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Wash Sale Rule */}
              <div style={{ background: V.bg2, border: `1px solid rgba(201,168,76,0.3)`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, fontWeight: 700, marginBottom: '10px' }}>The Wash Sale Rule</div>
                {[
                  'If you sell a security at a loss and buy a substantially identical security within 30 days before or after the sale — the IRS disallows the loss for tax purposes.',
                  'For stock traders this is straightforward. For options traders it is complex.',
                  'Relevant scenarios:\n— Closing a losing CSP and immediately opening a new CSP on the same stock may trigger wash sale rules.\n— Getting assigned shares and selling them at a loss while having open puts on the same stock.',
                  'The wash sale rule does NOT apply to Section 1256 contracts (index options). Another advantage of SPX trading.',
                  'Work with a tax professional to understand how wash sales may affect your specific situation.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '10px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Assignment and Cost Basis */}
              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, fontWeight: 700, marginBottom: '10px' }}>Assignment and Cost Basis</div>
                {[
                  'When you are assigned on a CSP — shares are put to you — your cost basis is calculated as:\n\nStrike Price\nMINUS Premium Originally Collected',
                  'Example:\nSold $165 put for $0.78 premium\nAssigned — shares put to you at $165\nCost basis: $165 − $0.78 = $164.22',
                  'This cost basis matters when you eventually sell the shares — either through assignment on a covered call or by selling directly.',
                  'Keep records of every premium collected. Your broker\'s tax forms (1099-B) will show the assignment but may not accurately reflect the reduced cost basis from premium collected.',
                  'Your tax professional needs to know about all premiums collected on positions that led to assignment.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '10px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Quarterly Estimated Taxes */}
              <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.gold, fontWeight: 700, marginBottom: '10px' }}>Quarterly Estimated Taxes</div>
                {[
                  'If trading is generating meaningful income you may be required to pay estimated taxes quarterly.',
                  'The IRS expects tax payments as income is earned — not just at year end.',
                  'Failing to pay quarterly estimated taxes can result in penalties even if you pay in full at tax time.',
                  'Consult a tax professional about whether quarterly estimated payments apply to your situation — especially in your first profitable year of trading.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '10px 0 0' : '0' }}>{p}</p>
                ))}
              </div>

              {/* Record Keeping */}
              <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>WHAT TO KEEP RECORDS OF</div>
                {[
                  'Every trade entry and exit (your broker provides this)',
                  'All premiums collected on positions that led to assignment',
                  'Cost basis adjustments from assignment premium',
                  'All premiums paid to close losing positions',
                  'The Tax Hub in this platform tracks trades automatically — export your records annually',
                  'Keep broker statements for at least 7 years',
                  'Document your trading as a business if applicable to your jurisdiction',
                  'Find a CPA who has experience with options traders — general CPAs often miss options-specific rules',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < 7 ? '10px' : 0 }}>
                    <span style={{ color: V.gold, flexShrink: 0, lineHeight: 1.5 }}>✓</span>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75 }}>{item}</span>
                  </div>
                ))}
                {onNavigate && (
                  <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', marginTop: '16px' }} onClick={() => onNavigate('taxhub')}>
                    Open Tax Hub →
                  </button>
                )}
              </div>

            </div>
          )}

          {chapterId === 'ch09' && section === 6 && (
            <div style={{ marginTop: '20px' }}>

              {/* Triple Witching card */}
              <div style={{ background: V.bg2, border: `1px solid rgba(196,122,122,0.4)`, borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.rose, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>CALENDAR AWARENESS</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', fontWeight: 500, color: V.gold, marginBottom: '12px', lineHeight: 1.35 }}>Triple Witching — Know This Date</h3>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: '0 0 14px', whiteSpace: 'pre-line' }}>{`Four times per year — on the third Friday of March, June, September, and December — three types of contracts expire simultaneously:\n\n— Stock index futures\n— Stock index options\n— Individual stock options\n\nThis is called Triple Witching.\n\nOn these days trading volume surges, price swings are exaggerated, and the market behaves unlike any other day of the year.\n\nFor options sellers this matters:\nVolatility is unpredictable.\nFills can be unusual.\nPositions can move sharply and quickly in ways that have nothing to do with the underlying stock.`}</p>
                <div style={{ background: V.bg3, border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, fontWeight: 700, marginBottom: '8px' }}>THE RULE:</div>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: 0, whiteSpace: 'pre-line' }}>{`Do not enter new positions on Triple Witching day.\nIf you have positions expiring on that day manage them the day before.\nBe aware. Be cautious.\nOr simply stand aside.`}</p>
                </div>

                {/* Triple Witching Dates sub-card */}
                <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '16px', marginTop: '12px' }}>
                  <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>TRIPLE WITCHING DATES</div>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: '0 0 12px', whiteSpace: 'pre-line' }}>{`Third Friday of:\nMarch — June — September — December\n\nMark these on your calendar at the start of every year.\nThey are visible in the Economic Calendar on your dashboard.`}</p>
                  <button style={{ ...btnGhost, padding: '7px 14px', fontSize: '12px' }} onClick={() => onNavigate && onNavigate('calendar')}>Open Economic Calendar →</button>
                </div>
              </div>

            </div>
          )}

          {chapterId === 'ch09' && section === 7 && (
            <div style={{ marginTop: '20px' }}>

              {/* Defined Risk Strategies */}
              <div style={{ background: V.bg2, border: `0.5px solid rgba(122,171,138,0.35)`, borderRadius: '10px', padding: '20px', marginBottom: '14px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.sage, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>DEFINED RISK STRATEGIES</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {['Bull Put Spread', 'Bear Call Spread'].map(s => (
                    <span key={s} style={{ background: 'rgba(122,171,138,0.12)', border: `0.5px solid rgba(122,171,138,0.3)`, borderRadius: '5px', padding: '3px 10px', fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.sage }}>{s}</span>
                  ))}
                </div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 12px' }}>
                  These have a capped, known maximum loss. Use the <strong style={{ color: V.text }}>1–2% Rule</strong>:
                </p>
                <div style={{ background: V.bg3, borderLeft: `3px solid ${V.sage}`, borderRadius: '6px', padding: '12px 14px', marginBottom: '12px' }}>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, lineHeight: 1.75, margin: 0 }}>
                    Risk no more than 1–2% of your account on the worst-case outcome of this trade. This protects you from a trade going completely against you.
                  </p>
                </div>
              </div>

              {/* Assignment Strategies */}
              <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.35)`, borderRadius: '10px', padding: '20px', marginBottom: '14px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>ASSIGNMENT STRATEGIES</div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {['Cash Secured Put', 'The Wheel'].map(s => (
                    <span key={s} style={{ background: 'rgba(201,168,76,0.1)', border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '5px', padding: '3px 10px', fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.goldL }}>{s}</span>
                  ))}
                </div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 12px' }}>
                  These are designed around the possibility of assignment. Getting assigned is not a loss — it is the strategy working. Use the <strong style={{ color: V.text }}>5–10% Concentration Rule</strong> instead:
                </p>
                <div style={{ background: V.bg3, borderLeft: `3px solid ${V.gold}`, borderRadius: '6px', padding: '12px 14px', marginBottom: '12px' }}>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, lineHeight: 1.75, margin: '0 0 8px' }}>
                    Capital required = Strike price × 100 × contracts
                  </p>
                  <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75, margin: 0 }}>
                    Keep this under 5–10% of your total account.
                  </p>
                </div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 8px' }}>
                  <strong style={{ color: V.text }}>Why a different number?</strong>
                </p>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
                  The real risk in The Wheel is not losing money on one trade. It is having too much of your account tied up in one company if that stock takes time to recover through covered call cycles. This is about <strong style={{ color: V.text }}>concentration</strong> — not loss.
                </p>
              </div>

              {/* Covered Call */}
              <div style={{ background: V.bg2, border: `0.5px solid rgba(240,237,230,0.1)`, borderRadius: '10px', padding: '20px', marginBottom: '14px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.muted, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>COVERED CALL</div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
                  You already own the shares. Selling the call generates income with no additional capital at risk.
                </p>
              </div>

              {/* Strike price note */}
              <div style={{ background: 'rgba(201,168,76,0.07)', border: `0.5px solid rgba(201,168,76,0.3)`, borderRadius: '10px', padding: '20px', marginBottom: '14px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, fontWeight: 700, marginBottom: '10px', letterSpacing: '0.02em' }}>IMPORTANT — STRIKE PRICE, NOT STOCK PRICE</div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 10px' }}>
                  Your capital requirement is based on your strike price — not the current stock price.
                </p>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, lineHeight: 1.8, margin: 0 }}>
                  A $250 stock with a 0.15 delta strike around $210 only requires $21,000 in capital — not $25,000. Always calculate concentration using your actual strike, not the stock's current price.
                </p>
              </div>

              {/* Quick Reference */}
              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '14px' }}>QUICK REFERENCE</div>
                {[
                  { strategy: 'Bull Put Spread',  rule: '1–2% max loss',          color: V.sage },
                  { strategy: 'Bear Call Spread', rule: '1–2% max loss',          color: V.sage },
                  { strategy: 'Cash Secured Put', rule: '5–10% concentration',    color: V.gold },
                  { strategy: 'Covered Call',     rule: 'No additional capital',  color: V.muted },
                  { strategy: 'The Wheel',        rule: '5–10% concentration',    color: V.gold },
                ].map((row, i, arr) => (
                  <div key={row.strategy} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < arr.length - 1 ? `0.5px solid rgba(240,237,230,0.06)` : 'none' }}>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text }}>{row.strategy}</span>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: row.color, fontWeight: 500 }}>→ {row.rule}</span>
                  </div>
                ))}
              </div>

            </div>
          )}

          {chapterId === 'ch10' && section === 1 && (
            <div style={{ marginTop: '20px' }}>

              {/* Opening quote */}
              <div style={{ background: 'rgba(201,168,76,0.06)', border: `0.5px solid rgba(201,168,76,0.2)`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '18px', color: V.gold, fontStyle: 'italic', lineHeight: 1.8, margin: '0 0 10px' }}>
                  "You cannot think your way out of a threat response. But you can remove the conditions that trigger it."
                </p>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted }}>— Trade Like a Woman</div>
              </div>

              {/* Main explanation */}
              <div style={{ background: V.bg2, borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 500, color: V.gold, marginBottom: '14px', lineHeight: 1.35 }}>
                  Why Your Financial Foundation Is a Nervous System Issue
                </h3>
                {[
                  'Your nervous system cannot tell the difference between a bad trade and a physical threat.',
                  'When money feels like survival — when losing this trade means something real about your safety, your rent, your family — your amygdala fires.',
                  'This is not a character flaw.\nThis is biology.\nYour nervous system is doing exactly what it was designed to do: protecting you from perceived threat.',
                  'The problem is that it cannot distinguish between a bear chasing you and a red position on a screen.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Three threat responses */}
              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>
                  The Three Threat Responses in Trading
                </div>
                {[
                  { label: 'FIGHT', body: 'You add to a losing position to make it back faster. You double down. You average in. You override the 2x rule because this one feels different.' },
                  { label: 'FLIGHT', body: 'You close a winning trade too early because you need the profit to feel safe. You exit at 30% instead of 50% because something feels wrong and you just want out.' },
                  { label: 'FREEZE', body: 'You cannot click the button to close a loss because closing it makes it real. You watch it go further against you. Paralyzed. Hoping it comes back.' },
                ].map((row, i) => (
                  <div key={row.label} style={{ borderLeft: `3px solid ${V.rose}`, paddingLeft: '14px', marginBottom: i < 2 ? '14px' : 0 }}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.rose, fontWeight: 600, marginBottom: '4px' }}>{row.label}</div>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75, margin: 0 }}>{row.body}</p>
                  </div>
                ))}
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, fontStyle: 'italic', lineHeight: 1.7, margin: '14px 0 0' }}>
                  Do you recognize yourself in any of these? Most traders recognize all three at different times. This is normal. This is nervous system. This is workable.
                </p>
              </div>

              {/* The Shift */}
              <div style={{ background: V.bg2, border: `1px solid rgba(122,171,138,0.3)`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', fontWeight: 500, color: V.sage, marginBottom: '14px', lineHeight: 1.35 }}>
                  What Changes When You Trade From Solid Ground
                </h3>
                {[
                  "When your trading capital is genuinely risk capital — money whose loss you could absorb without your life changing — the amygdala quiets.",
                  "Not completely. Not immediately.\nBut the signal is different.",
                  "'I might lose $500 on this trade' feels different in the body when $500 is genuinely surplus than when $500 is grocery money.",
                  "The rules do not change.\nThe position sizing does not change.\nBut your ability to follow them — calmly, consistently, without the nervous system hijacking your decisions — changes completely.",
                  "Trading from solid ground is the first and most powerful inner work practice available to you.",
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Three Tier Support */}
              <div style={{ background: V.bg2, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>
                  Three Levels of Support Available to You
                </div>
                {[
                  {
                    eyebrow: 'DAILY PRACTICE',
                    eyebrowColor: V.gold,
                    borderColor: V.gold,
                    title: 'Meditations',
                    body: 'Ground yourself before every session. Return to center when it closes. Two minutes or five — every day. Small consistent practice is more powerful than occasional intensity. Available in the Inner Work section anytime you need it.',
                    btnLabel: 'Open Meditations →',
                    btnView: 'meditations',
                    btnGoldStyle: false,
                  },
                  {
                    eyebrow: 'ONGOING INNER WORK',
                    eyebrowColor: V.gold,
                    borderColor: V.gold,
                    title: 'The Inner Work Section',
                    body: 'The deeper patterns that show up in your trading behavior over time. IFS-informed self-inquiry. Pre and post session check-ins. The Belief Mirror. For when you notice a pattern repeating and want to understand what is driving it.',
                    btnLabel: 'Open Inner Work →',
                    btnView: 'innerwork',
                    btnGoldStyle: false,
                  },
                  {
                    eyebrow: 'PROFESSIONAL SUPPORT',
                    eyebrowColor: V.sage,
                    borderColor: V.sage,
                    title: 'Somatic Experiencing with Erin Doumert',
                    body: 'When the patterns run deeper than self-inquiry can reach.\n\nErin is a Somatic Experiencing Practitioner and experienced trader who works specifically with traders on the nervous system patterns that override everything the mind knows.\n\nThis is not mindset coaching. This is not meditation tips. This is deep precise body-based nervous system work — the kind that actually changes how you respond in the moment when it counts.',
                    btnLabel: 'Learn More About Erin\'s Work →',
                    btnView: 'innerwork_resources',
                    btnGoldStyle: true,
                  },
                ].map((tier, i) => (
                  <div key={tier.title} style={{ background: V.bg3, borderLeft: `3px solid ${tier.borderColor === V.sage ? V.sage : V.gold}`, borderRadius: '8px', padding: '14px', marginBottom: i < 2 ? '10px' : 0 }}>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: tier.eyebrowColor, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '4px' }}>{tier.eyebrow}</div>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.text, fontWeight: 600, marginBottom: '8px' }}>{tier.title}</div>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, margin: '0 0 10px', whiteSpace: 'pre-line' }}>{tier.body}</p>
                    <button onClick={() => onNavigate && onNavigate(tier.btnView)}
                      style={tier.btnGoldStyle
                        ? { padding: '8px 16px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '6px', color: V.goldL, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px' }
                        : { padding: '8px 16px', background: 'transparent', border: `0.5px solid ${V.border}`, borderRadius: '6px', color: V.muted, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '12px' }
                      }>{tier.btnLabel}</button>
                  </div>
                ))}
              </div>

              {/* Closing reflection */}
              <div style={{ background: V.bg2, borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '16px', marginTop: '4px' }}>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', lineHeight: 1.9, margin: 0, whiteSpace: 'pre-line' }}>
                  {'The inner work and the outer work are the same work.\n\nEvery time you follow your rules under pressure you are training your nervous system. Every time you sit with discomfort instead of reacting you are building the capacity that makes consistent trading possible.\n\nThe market is a nervous system test. Every session. What you practice here — breathing, pausing, observing — is the same skill that keeps you from clicking when you should not.\n\nYou already have everything you need to do this work. This section exists to remind you of that.'}
                </p>
              </div>

            </div>
          )}

          {chapterId === 'ch06' && section === 3 && (
            <div style={{ marginTop: '20px' }}>

              {/* Opening card */}
              <div style={{ background: V.bg2, borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 500, color: V.gold, marginBottom: '10px', lineHeight: 1.35 }}>
                  Earnings and Implied Volatility — What Every CSP Seller Must Know
                </h3>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
                  Two forces that can work powerfully for you or against you — depending on whether you understand them.
                </p>
              </div>

              {/* ── EARNINGS ── */}
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', fontWeight: 500, color: V.gold, marginBottom: '12px', lineHeight: 1.35 }}>Earnings Reports</h3>

              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.18)`, borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
                {[
                  'Every publicly traded company reports earnings four times per year.',
                  'On earnings day the stock can move dramatically — up or down — in after-hours trading before you can react.',
                  'A stock that has been perfectly behaved for months can gap down 15–20% overnight on a single earnings report.',
                  'For CSP sellers this is critical:\n\nIf you are short a put and the stock gaps below your strike on earnings — you have no time to close the position. The gap happens in after-hours. Your order cannot execute.',
                  'THE RULE: Never hold a short option through an earnings report. Close the position or do not enter within 2 weeks of earnings.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: i === 4 ? V.text : V.muted, lineHeight: 1.9, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line', fontWeight: i === 4 ? 600 : 400 }}>{p}</p>
                ))}
              </div>

              {/* How to check earnings */}
              <div style={{ background: V.bg2, border: `1px solid rgba(201,168,76,0.25)`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.goldL, fontWeight: 600, marginBottom: '10px' }}>How to Check Earnings Dates in TOS</div>
                {[
                  'In ThinkorSwim go to the Trade tab',
                  'Pull up your stock symbol',
                  "Look for the earnings icon on the chart — a small 'E' marker on the date",
                  'OR go to the Options Chain and look for asterisks (*) next to expiration dates that contain an earnings event',
                  'OR go to Analyze tab → Earnings to see all upcoming earnings for your watchlist',
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < 4 ? '8px' : 0 }}>
                    <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '11px', color: V.gold, flexShrink: 0, marginTop: '2px' }}>{i + 1}.</span>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0 }}>{s}</p>
                  </div>
                ))}
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0', fontStyle: 'italic' }}>
                  Avoid any expiration that falls after an upcoming earnings date within your trade window.
                </p>
              </div>

              {/* Market Brief Connection */}
              <div style={{ background: V.bg2, border: `1px solid rgba(122,171,138,0.3)`, borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.sage, fontWeight: 600, marginBottom: '8px' }}>Check This Every Morning</div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 12px' }}>
                  The Market Brief on your dashboard surfaces earnings announcements and volatility conditions every morning before market open.
                  Make it part of your daily routine: Open the Market Brief first. Check what is moving. Check what earnings are coming. Then open your charts.
                  You will never be caught off guard by an earnings report you did not see coming.
                </p>
              </div>

              {/* ── IMPLIED VOLATILITY ── */}
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', fontWeight: 500, color: V.gold, marginTop: '24px', marginBottom: '12px', lineHeight: 1.35 }}>
                Implied Volatility — Sell High, Benefit From the Collapse
              </h3>

              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.18)`, borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
                {[
                  "Implied Volatility (IV) is the market's expectation of how much a stock will move in the future.",
                  'High IV = market expects big moves = options are EXPENSIVE = more premium for sellers to collect.\n\nLow IV = market expects calm = options are CHEAP = less premium available.',
                  'As a premium seller you want to sell when IV is elevated and benefit when it returns to normal.',
                  "This is called IV Crush — and it is one of the most powerful forces working in a seller's favor.",
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.9, margin: i > 0 ? '12px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* IV Crush */}
              <div style={{ background: V.bg2, border: `1px solid rgba(122,171,138,0.3)`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.sage, fontWeight: 600, marginBottom: '8px' }}>What is IV Crush?</div>
                {[
                  "IV spikes before earnings because nobody knows what the report will show. The uncertainty inflates option prices.",
                  "The moment earnings are announced — regardless of whether the news is good or bad — the uncertainty resolves.\n\nIV collapses immediately. Options deflate rapidly. Sellers who entered BEFORE earnings at high IV collect rich premium.",
                  "BUT — the stock can still move dramatically enough to overwhelm the IV crush benefit.",
                  "This is why the rule is to AVOID earnings in this program — not to exploit IV crush.\n\nIV crush exploitation is an advanced strategy for Tier 2.",
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '10px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* VIX Connection */}
              <div style={{ background: V.bg2, borderLeft: '3px solid #c9a84c', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.goldL, fontWeight: 600, marginBottom: '10px' }}>Reading IV With the VIX</div>
                {[
                  { label: null,        text: "The VIX is the market's overall implied volatility index." },
                  { label: 'VIX below 15:', text: 'Options are cheap across the market. Less premium available. Consider reducing position size.' },
                  { label: 'VIX 15–25:', text: 'Normal volatility environment. Good conditions for premium selling.' },
                  { label: 'VIX above 25:', text: 'Elevated volatility. Options are expensive. Rich premium available. BUT markets are uncertain — position size carefully.' },
                  { label: 'VIX above 35:', text: 'Fear is high. Premium is very rich. Also means markets are moving fast. Experienced sellers can profit significantly here. Beginners should reduce size or wait for clarity.' },
                ].map((row, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '8px 0 0' : '0' }}>
                    {row.label && <span style={{ color: V.goldL, fontWeight: 600 }}>{row.label} </span>}{row.text}
                  </p>
                ))}
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '12px 0 0', fontStyle: 'italic' }}>
                  Check the VIX every morning — not just before entering a position. It takes five seconds and tells you whether conditions favor selling before you even open a chart.
                  <br /><br />
                  The VIX is shown in your daily Market Brief on the dashboard. Make it the first number you look at each morning.
                </p>
              </div>

              {/* IV Rank */}
              <div style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.18)`, borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.goldL, fontWeight: 600, marginBottom: '8px' }}>IV Rank — The Better Measurement</div>
                {[
                  'Raw IV numbers vary by stock. A 30% IV on one stock might be high. On another it might be low.',
                  'IV Rank solves this: It shows where current IV sits relative to the past year.\n\nIV Rank of 80 means IV is higher than it has been 80% of the time over the past year. That is a rich premium environment.\n\nIV Rank of 20 means IV is low relative to recent history. Less premium available.',
                  'In ThinkorSwim: Add IV Rank to your options chain columns. Look for IV Rank above 30 before entering new positions.',
                ].map((p, i) => (
                  <p key={i} style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: i > 0 ? '10px 0 0' : '0', whiteSpace: 'pre-line' }}>{p}</p>
                ))}
              </div>

              {/* Pre-trade checklist */}
              <div style={{ background: V.bg2, border: `1px solid rgba(201,168,76,0.25)`, borderRadius: '10px', padding: '20px', marginTop: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '12px' }}>PRE-TRADE CHECKLIST — IV AND EARNINGS</div>
                {[
                  'Opened Market Brief this morning ✓',
                  'Checked earnings date — none within 2 weeks ✓',
                  'Checked VIX in Market Brief — conditions favorable ✓',
                  'IV Rank above 30 ✓',
                  'Premium at target strike is worth the trade ✓',
                  'All other setup criteria met — trend, structure, momentum ✓',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: i < 5 ? '10px' : 0 }}>
                    <span style={{ color: V.gold, fontSize: '14px', flexShrink: 0, lineHeight: 1.4 }}>☐</span>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.75 }}>{item}</span>
                  </div>
                ))}
                {onNavigate && (
                  <button style={{ ...btnGhost, padding: '6px 14px', fontSize: '12px', marginTop: '16px' }} onClick={() => onNavigate('marketbrief')}>
                    Open Market Brief →
                  </button>
                )}
              </div>

            </div>
          )}

          {chapterId === 'ch02' && section === 0 && (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '16px 20px', marginTop: '20px' }}>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:0 }}>
                Charts tell you what has happened. Indicators — which you will learn in Chapter 3 — tell you how stretched the rubber band is right now. You need both. But price comes first. Always start with the raw chart.
              </p>
            </div>
          )}
          {chapterId !== 'ch02' && chapterId !== 'ch01' && (() => {
            const nav = CHAPTER_BOTTOM_NAV[chapterId];
            if (!nav) return null;
            return (
              <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '8px', padding: '16px 20px', marginTop: '24px' }}>
                {nav.prompt && <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', margin: '0 0 12px', lineHeight: 1.7 }}>{nav.prompt}</p>}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {nav.primary && (chapterId !== 'ch06' || section === 2 || section === 4) && <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }} onClick={() => { if (nav.primary.strategy) { try { localStorage.setItem('tlaw_strategy_selected', nav.primary.strategy); } catch {} onPractice && onPractice(`chapter_${chapterId}`, section); } onNavigate && onNavigate(nav.primary.view); }}>{nav.primary.label}</button>}
                  {nav.secondary && !(chapterId === 'ch06' && section === 4) && <button style={{ ...btnGhost, padding: '10px 18px', fontSize: '13px' }} onClick={() => onNavigate && onNavigate(nav.secondary.view)}>{nav.secondary.label}</button>}
                </div>
              </div>
            );
          })()}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
            {(() => {
              // Previous chapter map — keeps navigation strictly inside the app
              const PREV_CHAPTER = { ch02:'ch01', ch03:'ch02', ch04:'ch03', ch05:'ch04', ch06:'ch05', ch07:'ch06', ch08:'ch07', ch09:'ch08', ch10:'ch09' };
              if (section > 0) {
                return <button style={btnGhost} onClick={() => setSection(s => s - 1)}>← Previous</button>;
              }
              const prev = PREV_CHAPTER[chapterId];
              if (prev && onNavigate) {
                return <button style={btnGhost} onClick={() => onNavigate(`chapter_${prev}`)}>← Previous Chapter</button>;
              }
              // ch01 (or unknown) at section 0: no previous chapter — go back to dashboard
              return <button style={btnGhost} onClick={() => onBack()}>← Back</button>;
            })()}
            <button style={btnGold} onClick={() => setSection(s => s + 1)}>{section < content.sections.length - 1 ? 'Next →' : 'Take Quiz →'}</button>
          </div>
        </div>
      ) : score !== null ? (
        <div>
          <div style={{ textAlign: 'center', padding: '32px 24px', background: V.bg2, borderRadius: '12px', border: `0.5px solid ${score >= 75 ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.3)'}` }}>
            <div style={{ ...mono, fontSize: '52px', color: score >= 75 ? V.sage : V.rose, marginBottom: '8px' }}>{score}%</div>
            <div style={{ fontSize: '14px', color: score >= 75 ? V.sage : V.rose, marginBottom: '6px' }}>
              {score >= 75 ? '✓ Chapter passed! Next chapter unlocked.' : '✗ Need 75% to pass. Review the sections and try again.'}
            </div>
            {score < 75 && (
              <button style={{ ...btnGold, marginTop: '16px' }} onClick={resetQuiz}>Try Again →</button>
            )}
          </div>
          <FeedbackCard chapterId={chapterId} chapterTitle={CHAPTER_FEEDBACK_TITLES[chapterId] || chapterId} type='chapter' onComplete={() => {}} />
          {chapterId === 'ch01' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: '0 0 8px' }}>
                You understand what options are and why the rubber band principle gives sellers an edge.
              </p>
              <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', lineHeight: 1.8, margin: '0 0 16px' }}>
                Now learn to read the chart that tells you when to act.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch02')}>
                Continue to Chapter 2 →
              </button>
            </div>
          ) : chapterId === 'ch02' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Can Now Read Price</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                You understand what candlesticks are telling you. You can identify trend direction from price structure. You know what volume confirms. You can recognize the patterns that signal reversals and continuations.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                But price alone doesn't tell you HOW STRETCHED the rubber band is right now. That is what momentum indicators measure. In Chapter 3 you will add Moving Average lines and RSI to your chart — measuring the same rubber band from a different angle.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch03')}>
                Continue to Chapter 3 →
              </button>
            </div>
          ) : chapterId === 'ch04' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Can Now Read the Market</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                You can read price action. You can measure momentum. You can identify structure. You can find the confluence.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 8px' }}>
                There is one more layer before you place your first trade: understanding how the OPTIONS THEMSELVES are priced.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 8px' }}>
                Chapter 5 teaches you the Greeks — the forces that determine how much an option is worth and how that value changes as the market moves.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                Delta will tell you probability. Theta will tell you why time works in your favor as a seller. These two Greeks are the foundation of every strategy you are about to learn.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch05')}>
                Continue to Chapter 5 →
              </button>
            </div>
          ) : chapterId === 'ch05' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Understand the Greeks</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                Delta tells you probability. Theta tells you why time works in your favor. Vega tells you when conditions are ripe to sell. Gamma tells you where to be careful.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                You have everything you need to understand why the strategies in the next four chapters work. Chapter 6 is your first selling strategy — the Cash Secured Put and Bull Put Spread. You will see Delta and Theta working exactly as you just learned.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch06')}>
                Continue to Chapter 6 →
              </button>
            </div>
          ) : chapterId === 'ch06' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Can Now Sell Cash Secured Puts</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                You understand how to collect premium, how to select strikes with Delta, and how to manage and roll your positions.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                Now learn the other side of the Wheel. Chapter 7 teaches Covered Calls — how to generate income from stock you already own.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch07')}>
                Continue to Chapter 7 →
              </button>
            </div>
          ) : chapterId === 'ch07' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Can Now Use Covered Calls</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                You can sell puts. You can sell calls. You understand assignment on both sides and how premium collection compounds over time.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                In Chapter 8 you will connect these two strategies into one continuous income system — the Wheel.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch08')}>
                Continue to Chapter 8 →
              </button>
            </div>
          ) : chapterId === 'ch08' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Understand the Wheel</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                CSPs and Covered Calls combined into one cycle. Cost basis reduction. Continuous premium collection. You can manage a full Wheel from entry to exit.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                Chapter 9 is the discipline beneath every strategy. Risk management is what keeps you in the game long enough for your edge to compound.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch09')}>
                Continue to Chapter 9 →
              </button>
            </div>
          ) : chapterId === 'ch09' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Can Manage Risk</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                Position sizing, the 2× loss rule, the daily max loss, cash as a position. You have a complete risk framework to follow forever.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                Chapter 10 is the final layer — trading psychology. The market will test everything you have learned. This chapter teaches you how to pass that test.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch10')}>
                Continue to Chapter 10 →
              </button>
            </div>
          ) : chapterId === 'ch10' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Are Ready to Trade</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                The foundation is complete. Strategy. Structure. Risk management. Psychology. You have everything you need to begin paper trading with discipline and confidence.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                The Readiness Test is the final assessment — 60 questions across 7 sections. A score of 75% unlocks your graduation review.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('test')}>
                Take the Readiness Test →
              </button>
            </div>
          ) : chapterId === 'ch03' ? (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px', marginTop: '24px' }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontSize:'20px', color:V.gold, fontWeight:400, margin:'0 0 12px' }}>You Can Now Read Momentum</h3>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, fontStyle:'italic', lineHeight:1.8, margin:'0 0 8px' }}>
                You understand what moving averages are showing you. You can read RSI and identify the CSP entry zone. You know when to stand aside in a choppy market.
              </p>
              <p style={{ fontFamily:"'Jost',sans-serif", fontSize:'13px', color:V.muted, lineHeight:1.8, margin:'0 0 16px' }}>
                In Chapter 4 you will add the final layer of reading: support, resistance, and the key price levels that tell you exactly where to place your strikes.
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px', width: '100%' }} onClick={() => onNavigate && onNavigate('chapter_ch04')}>
                Continue to Chapter 4 →
              </button>
            </div>
          ) : (
            <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '16px 20px', marginTop: '24px' }}>
              <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '12px', margin: '0 0 12px' }}>
                Want to see these concepts in action?
              </p>
              <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }} onClick={() => onNavigate && onNavigate('full_trade_simulator')}>
                Open The Full Trade Simulator →
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ ...serif, fontSize: '22px', fontWeight: 500, marginBottom: '6px' }}>Chapter Quiz</div>
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px' }}>Score 75% or higher to unlock the next chapter.</p>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '20px' }}>
            {content.quiz.map((_, i) => (
              <div key={i} style={{ width: '9px', height: '9px', borderRadius: '50%', background: i < quizQ ? V.sage : i === quizQ ? V.gold : V.border, transition: 'background .3s', flexShrink: 0 }}/>
            ))}
            <span style={{ fontSize: '11px', color: V.muted, marginLeft: '8px' }}>
              Question {quizQ + 1} of {content.quiz.length}
            </span>
          </div>
          {(() => {
            const q = content.quiz[quizQ];
            const picked = quizAnswers[quizQ];
            const isCorrect = picked === q.correct;
            return (
              <div style={card}>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '16px', lineHeight: 1.65 }}>
                  <span style={{ ...mono, color: V.gold, marginRight: '8px' }}>{quizQ + 1}.</span>{q.q}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {q.options.map((opt, oi) => {
                    let bg = 'transparent', border = V.border, color = V.text;
                    if (quizFeed) {
                      if (oi === q.correct)  { bg = 'rgba(122,171,138,0.1)'; border = 'rgba(122,171,138,0.4)'; color = V.sage; }
                      else if (oi === picked) { bg = 'rgba(196,122,122,0.1)'; border = 'rgba(196,122,122,0.35)'; color = V.rose; }
                      else                   { color = V.muted; }
                    } else if (picked === oi) {
                      bg = 'rgba(201,168,76,0.08)'; border = 'rgba(201,168,76,0.4)'; color = V.goldL;
                    }
                    return (
                      <div key={oi} onClick={() => pickAnswer(oi)}
                        style={{ padding: '10px 14px', borderRadius: '8px', border: `0.5px solid ${border}`, background: bg, color, fontSize: '13px', cursor: quizFeed ? 'default' : 'pointer', lineHeight: 1.5, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {quizFeed && oi === q.correct && <span style={{ color: V.sage, flexShrink: 0 }}>✓</span>}
                        {quizFeed && oi === picked && oi !== q.correct && <span style={{ color: V.rose, flexShrink: 0 }}>✗</span>}
                        {opt}
                      </div>
                    );
                  })}
                </div>
                {quizFeed && (
                  <div style={{ marginTop: '14px', background: V.bg3, borderRadius: '6px', padding: '12px', borderLeft: `2px solid ${V.gold}` }}>
                    <span style={{ fontSize: '11px', color: isCorrect ? V.sage : V.rose }}>
                      {isCorrect ? '✓ Correct  ' : '✗ Not quite — '}
                    </span>
                    <span style={{ color: V.goldL, fontSize: '12px' }}>Key insight: </span>
                    <span style={{ fontSize: '12px', color: V.muted, lineHeight: 1.65 }}>{q.explain}</span>
                  </div>
                )}
              </div>
            );
          })()}
          {quizFeed ? (
            <button style={{ ...btnGold, width: '100%', padding: '14px', marginTop: '12px' }} onClick={advanceQuiz}>
              {quizQ < content.quiz.length - 1 ? 'Next Question →' : 'See Results →'}
            </button>
          ) : (
            <p style={{ fontSize: '12px', color: V.dim, textAlign: 'center', marginTop: '12px' }}>
              Select an answer above to continue.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ChapterViewer
